// Plain helpers with no screen of their own.

const LS_PREFIX = "hfl:";

/** A setting from localStorage (strings only), or `fallback`. Storage can be blocked: never throws. */
export function lsGet(key: string, fallback: string): string {
  try { return localStorage.getItem(LS_PREFIX + key) ?? fallback; } catch { return fallback; }
}
export function lsSet(key: string, value: string): void {
  try { localStorage.setItem(LS_PREFIX + key, value); } catch { /* storage blocked: the setting just isn't kept */ }
}
/** A JSON value from localStorage, or `fallback` when missing or unreadable. */
export function lsJson<T>(key: string, fallback: T): T {
  try { return (JSON.parse(lsGet(key, "null")) as T | null) ?? fallback; } catch { return fallback; }
}

/** A short unique id, sortable by creation time. */
export const uid = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

/** A number typed by the user ("2,5" or "2.5"), or null. */
export function num(s: string | null | undefined): number | null {
  const n = parseFloat(String(s ?? "").replace(",", ".").replace(/\s/g, ""));
  return Number.isFinite(n) ? n : null;
}

const pad = (n: number) => String(n).padStart(2, "0");
/** The local date as YYYY-MM-DD. */
export function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
/** The UTC date as YYYY-MM-DD (for weather queries in GMT). */
export const isoDay = (ts: number): string => new Date(ts).toISOString().slice(0, 10);
/** Value for an <input type="datetime-local">. */
export function toLocalInput(ts: number): string {
  const d = new Date(ts);
  return `${dayKey(ts)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export { distanceKm } from "../shared/geo.ts";

/** The error a storage call gives when it doesn't answer (see db.ts); shown to the user as "close and reopen". */
export const timeoutError = (): Error => Object.assign(new Error("Storage did not answer"), { name: "TimeoutError" });
export const errorName = (e: unknown): string => (e instanceof Error || e instanceof DOMException ? e.name : "");
export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));
